import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Coffee, 
  Clock, 
  TrendingUp, 
  ChevronDown,
  ChevronUp,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Timer,
  UtensilsCrossed
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Break Stats Per Employee Component
export const EmployeeBreakStats = ({ days = 30 }) => {
  const [breakStats, setBreakStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchBreakStats();
  }, [days]);

  const fetchBreakStats = async () => {
    try {
      const response = await axios.get(`${API}/overtime/break-stats?days=${days}`);
      setBreakStats(response.data);
    } catch (error) {
      console.error('Failed to fetch break stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="flex items-center justify-center py-8">
          <div className="spinner" />
        </CardContent>
      </Card>
    );
  }

  const displayStats = isExpanded ? breakStats : breakStats.slice(0, 5);
  const hasMore = breakStats.length > 5;

  return (
    <Card className="border-0 shadow-lg" data-testid="employee-break-stats">
      <CardHeader className="pb-2 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            <Coffee className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            <CardTitle className="font-['Manrope'] text-base sm:text-lg">Break Time by Employee</CardTitle>
          </div>
          <Badge variant="outline" className="text-xs self-start sm:self-auto">Last {days} days</Badge>
        </div>
        <CardDescription className="text-xs sm:text-sm">Average break duration per employee</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 sm:space-y-3 p-4 sm:p-6 pt-0">
        {breakStats.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No employees found</p>
        ) : breakStats.every(e => e.total_breaks === 0) ? (
          <div className="text-center py-6">
            <Coffee className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No breaks taken yet</p>
            <p className="text-xs text-slate-400">Break data will appear when employees take breaks</p>
          </div>
        ) : (
          <>
            {displayStats.map((emp, index) => (
              <div 
                key={emp.employee_id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors gap-2"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs sm:text-sm font-medium text-amber-700 shrink-0">
                    {emp.employee_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{emp.employee_name}</p>
                    <p className="text-xs text-slate-500">{emp.total_breaks} breaks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm pl-9 sm:pl-0">
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-orange-600">
                      <Coffee className="w-3 h-3" />
                      <span className="font-medium">{emp.avg_breakfast_minutes.toFixed(0)}m</span>
                    </div>
                    <span className="text-xs text-slate-400">Bfast</span>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-amber-600">
                      <UtensilsCrossed className="w-3 h-3" />
                      <span className="font-medium">{emp.avg_lunch_minutes.toFixed(0)}m</span>
                    </div>
                    <span className="text-xs text-slate-400">Lunch</span>
                  </div>
                  <div className="text-center min-w-[50px] sm:min-w-[60px]">
                    <span className={`font-medium ${emp.total_break_minutes > 90 * emp.total_breaks / 2 ? 'text-red-600' : 'text-slate-700'}`}>
                      {Math.round(emp.total_break_minutes)}m
                    </span>
                    <p className="text-xs text-slate-400">Total</p>
                  </div>
                </div>
              </div>
            ))}
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-slate-600"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Show All ({breakStats.length - 5} more)
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Punctuality Per Employee Component
export const EmployeePunctuality = ({ days = 30 }) => {
  const [punctuality, setPunctuality] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchPunctuality();
  }, [days]);

  const fetchPunctuality = async () => {
    try {
      const response = await axios.get(`${API}/overtime/punctuality?days=${days}`);
      setPunctuality(response.data);
    } catch (error) {
      console.error('Failed to fetch punctuality:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="flex items-center justify-center py-8">
          <div className="spinner" />
        </CardContent>
      </Card>
    );
  }

  const displayStats = isExpanded ? punctuality : punctuality.slice(0, 5);
  const hasMore = punctuality.length > 5;

  const getPunctualityColor = (percent) => {
    if (percent >= 90) return 'text-green-600';
    if (percent >= 75) return 'text-amber-600';
    return 'text-red-600';
  };

  const getPunctualityBg = (percent) => {
    if (percent >= 90) return 'bg-green-100';
    if (percent >= 75) return 'bg-amber-100';
    return 'bg-red-100';
  };

  return (
    <Card className="border-0 shadow-lg" data-testid="employee-punctuality">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <CardTitle className="font-['Manrope'] text-lg">Punctuality by Employee</CardTitle>
          </div>
          <Badge variant="outline" className="text-xs">Last {days} days</Badge>
        </div>
        <CardDescription>On-time arrival rate per employee</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {punctuality.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No employees found</p>
        ) : punctuality.every(e => e.total_shifts === 0) ? (
          <div className="text-center py-6">
            <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No scheduled shifts yet</p>
            <p className="text-xs text-slate-400">Add shifts to roster to track punctuality</p>
          </div>
        ) : (
          <>
            {displayStats.map((emp) => (
              <div 
                key={emp.employee_id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full ${getPunctualityBg(emp.punctuality_percent)} flex items-center justify-center text-sm font-medium ${getPunctualityColor(emp.punctuality_percent)}`}>
                    {emp.employee_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{emp.employee_name}</p>
                    <p className="text-xs text-slate-500">{emp.total_shifts} scheduled shifts</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-green-700">{emp.on_time_count}</span>
                    <XCircle className="w-4 h-4 text-red-500 ml-2" />
                    <span className="text-red-700">{emp.late_count}</span>
                  </div>
                  <div className={`text-lg font-bold ${getPunctualityColor(emp.punctuality_percent)}`}>
                    {emp.punctuality_percent.toFixed(0)}%
                  </div>
                </div>
              </div>
            ))}
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-slate-600"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Show All ({punctuality.length - 5} more)
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Period Progress Per Employee Component
export const EmployeePeriodProgress = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchEmployeeProgress();
  }, []);

  const fetchEmployeeProgress = async () => {
    try {
      const response = await axios.get(`${API}/overtime/all-employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employee progress:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="flex items-center justify-center py-8">
          <div className="spinner" />
        </CardContent>
      </Card>
    );
  }

  const displayEmployees = isExpanded ? employees : employees.slice(0, 5);
  const hasMore = employees.length > 5;

  const getProgressColor = (progress, overtime) => {
    if (overtime > 0) return 'bg-red-500';
    if (progress >= 90) return 'bg-amber-500';
    if (progress >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusBadge = (info) => {
    if (info.overtime_hours > 0) {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
          +{info.overtime_hours.toFixed(1)}h OT
        </Badge>
      );
    }
    if (info.is_overtime_warning) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
          Near limit
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
        On track
      </Badge>
    );
  };

  return (
    <Card className="border-0 shadow-lg" data-testid="employee-period-progress">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            <CardTitle className="font-['Manrope'] text-lg">Period Progress by Employee</CardTitle>
          </div>
          {employees[0] && (
            <Badge variant="outline" className="text-xs capitalize">
              {employees[0].period_info.period_type}
            </Badge>
          )}
        </div>
        <CardDescription>
          Hours worked this period per employee
          {employees[0]?.period_info?.period_start && (
            <span className="block text-xs text-slate-400 mt-1">
              {(() => {
                // Extract date directly from ISO string to preserve original timezone's date
                const startStr = employees[0].period_info.period_start;
                const endStr = employees[0].period_info.period_end;
                const startDate = startStr.substring(0, 10); // YYYY-MM-DD
                const endDate = endStr.substring(0, 10);
                const formatLocalDate = (dateStr) => {
                  const [year, month, day] = dateStr.split('-');
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${parseInt(day)} ${months[parseInt(month) - 1]}`;
                };
                const formatLocalDateWithYear = (dateStr) => {
                  const [year, month, day] = dateStr.split('-');
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
                };
                return `${formatLocalDate(startDate)} - ${formatLocalDateWithYear(endDate)}`;
              })()}
              {' • '}{employees[0].period_info.days_remaining} days left
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {employees.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No employee data available</p>
        ) : (
          <>
            {displayEmployees.map((emp) => {
              const info = emp.period_info;
              return (
                <div 
                  key={emp.employee_id}
                  className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        info.overtime_hours > 0 
                          ? 'bg-red-100 text-red-700' 
                          : info.is_overtime_warning 
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {emp.employee_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{emp.employee_name}</p>
                        <p className="text-xs text-slate-500">
                          {info.hours_remaining.toFixed(1)}h remaining
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(info)}
                      <div className="text-right">
                        <span className="text-lg font-bold text-slate-900">{info.hours_worked.toFixed(1)}</span>
                        <span className="text-sm text-slate-500">/{info.standard_hours}h</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`absolute left-0 top-0 h-full rounded-full transition-all ${getProgressColor(info.progress_percent, info.overtime_hours)}`}
                      style={{ width: `${Math.min(info.progress_percent, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                    <span>{info.progress_percent.toFixed(0)}%</span>
                    <span>{info.days_remaining} days left</span>
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-slate-600"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Show All ({employees.length - 5} more)
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
